import { type ReactElement } from 'react';
import {
  PolicyKey,
  PolicyMode,
  type PolicyModeType,
} from '@kirin/api-client/contracts/policies';
import {
  useUserPolicies,
  useToggleUserPolicy,
} from '@/hooks/api/useUserPolicies';
import { useToast } from '@/hooks/use-toast';
import { createMutationHandler } from '@/hooks/mutationHelpers';
import { MODE_LABELS } from '@/components/policylist/constants';
import { SecureAiCodingEnforcementPage } from '@/components/AiInstructionCatalog/SecureAiCodingEnforcementPage';

/**
 * KIR-3816: personal Secure AI Coding Enforcement page. Reads/writes the
 * `ai-instruction-catalog` policy mode via the account policy hooks and renders
 * the shared enforcement page.
 */
export default function SecureAiCodingTab(): ReactElement {
  const { data: policies } = useUserPolicies();
  const togglePolicy = useToggleUserPolicy();
  const { toast } = useToast();

  const policy = policies?.find(p => p.key === PolicyKey.AiInstructionCatalog);
  const mode = (policy?.mode ?? PolicyMode.Off) as PolicyModeType;
  const policyName = policy?.name ?? 'Secure AI Coding Enforcement';

  // Route through createMutationHandler so a mode change shows the same
  // success/error toast as every other policy on the Policies page.
  const onModeChange = (next: PolicyModeType): void => {
    void createMutationHandler(togglePolicy, toast, {
      successMessage: `Policy ${policyName} set to ${MODE_LABELS[next]}`,
      errorMessage: `Failed to update ${policyName}`,
    })({
      policyKey: PolicyKey.AiInstructionCatalog,
      enabled: next !== PolicyMode.Off,
      mode: next,
    });
  };

  return (
    <SecureAiCodingEnforcementPage
      canEdit
      mode={mode}
      supportsEnforce={policy?.supportsEnforce ?? false}
      enforceDisabledReason={policy?.enforceDisabledReason}
      enforceTooltip={policy?.enforceTooltip}
      onModeChange={onModeChange}
      modePending={togglePolicy.isPending}
    />
  );
}
